// Batches DOM reads and writes so a screenful of independent callbacks costs ONE forced
// synchronous layout instead of one per callback.
//
// The problem this solves: a component that measures (`scrollHeight`, `clientWidth`, …) and then
// mutates (toggle a class, set an attribute) invalidates layout with its write, so the *next*
// component's measurement forces the browser to lay the page out again. Twelve cards each doing
// read-then-write in the same frame = twelve full layouts. Safari has no equivalent of Chrome's
// partial-layout optimization, so this is where its jank comes from.
//
// Queue the read with `measure()` and the write with `mutate()` and the layout is computed once:
//
//   measure(() => {
//     const clamped = el.scrollHeight > el.clientHeight
//     mutate(() => button.classList.toggle('hidden', !clamped))
//   })
//
// A `mutate()` queued from inside a `measure()` still runs in the same frame — reads are drained
// first, then everything that landed in the write queue. Anything queued during the write phase
// runs on the next frame, which is what you want: it means layout was dirtied again.

let reads = []
let writes = []
let scheduled = false
let flushing = false

function run(callback) {
  try {
    callback()
  } catch (error) {
    // One bad callback must not strand the rest of the frame's queue.
    console.error('layout_batch callback failed:', error)
  }
}

function flush() {
  flushing = true

  try {
    // Snapshot before draining: a read may queue another read, which belongs in the next batch
    // rather than extending the phase we're iterating.
    const currentReads = reads
    reads = []
    currentReads.forEach(run)

    // Snapshotted after the reads ran, so writes queued from inside a `measure()` are included.
    const currentWrites = writes
    writes = []
    currentWrites.forEach(run)
  } finally {
    flushing = false
    scheduled = false
  }

  // Anything queued during the write phase dirtied layout again, so it gets its own frame.
  if (reads.length || writes.length) schedule()
}

function schedule() {
  // `flushing` suppresses a redundant frame request from callbacks that queue more work — the
  // tail of flush() schedules once if anything is actually left over.
  if (scheduled || flushing) return
  scheduled = true

  const raf = globalThis.requestAnimationFrame
  if (raf) {
    raf(flush)
  } else {
    setTimeout(flush, 16)
  }
}

// Queue a DOM read. Runs before every `mutate()` queued in the same frame.
export function measure(callback) {
  reads.push(callback)
  schedule()
}

// Queue a DOM write. Runs after every `measure()` queued in the same frame.
export function mutate(callback) {
  writes.push(callback)
  schedule()
}
